import { useEffect, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import type { AccordionEventKey } from "react-bootstrap/esm/AccordionContext";
import {
  FiUser,
  FiUsers,
  FiBookOpen,
  FiFileText,
  FiGrid,
  FiPrinter,
} from "react-icons/fi";
import { ReviewSection, ReviewField } from "@/components/shared/ReviewSection";
import Card from "@/components/shared/Card";
import { useChildEntities, useCoreData } from "@/queries";
import { useStateChildData } from "./useStateChildData";
import { usePreschoolNames } from "./usePreschoolNames";
import { useReportExport } from "@/components/state-level-report/hooks/useReportExport";
import { escapeCsv } from "@/utils/reportCsv";
import { formatDateOnly } from "@/utils/dates";
import { type CHLD } from "@/models/chld";
import { type FAMI } from "@/models/fami";
import { type ENRL, type CoreDataModel } from "@/models";
import {
  getGenderText,
  getHispanicIndicatorText,
  getRaceText,
  getIEPStatusText,
  getContractTypeText,
  getProgramTypeText,
  getEligibilityStatusText,
  getServiceReasonsText,
} from "./childDataViewTransformers";

type WithPreschoolName<T> = T & { PreschoolName?: string };

type Agency = {
  key: string;
  leaName: string;
  currentChld: WithPreschoolName<CHLD>;
  isCurrent: boolean;
  famiRecords: FAMI[];
  enrlRecords: WithPreschoolName<ENRL>[];
};

function getChildName(c: CHLD): string {
  return [
    c.ChildFirstName,
    c.ChildMiddleName,
    c.ChildLastName,
    c.ChildNameSuffix,
  ]
    .filter(Boolean)
    .join(" ");
}

function buildAgencies(
  chld: WithPreschoolName<CHLD>[],
  fami: FAMI[],
  enrl: WithPreschoolName<ENRL>[],
  preschoolNameMap: Record<string, string>,
  leaNameMap: Record<string, string>
): Agency[] {
  const byLea = new Map<string, WithPreschoolName<CHLD>[]>();
  chld.forEach((c) => {
    const key = c.ReportingLEA;
    if (!byLea.has(key)) byLea.set(key, []);
    byLea.get(key)!.push(c);
  });

  const agencies: Agency[] = [];
  byLea.forEach((records, reportingLEA) => {
    const openEnded = records.filter((c) => !c.EffectiveEndDate);
    const sortedByStart = [...records].sort((a, b) =>
      (b.EffectiveStartDate ?? "").localeCompare(a.EffectiveStartDate ?? "")
    );
    const currentChld =
      [...openEnded].sort((a, b) =>
        (b.EffectiveStartDate ?? "").localeCompare(a.EffectiveStartDate ?? "")
      )[0] ?? sortedByStart[0];

    if (!currentChld) return;

    const famiRecords = fami.filter(
      (f) => f.ReportingLEA === reportingLEA && !f.EffectiveEndDate
    );
    const enrlRecords = [...enrl]
      .filter((e) => e.ReportingLEA === reportingLEA)
      .sort((a, b) =>
        (b.EnrollmentStartDate ?? "").localeCompare(a.EnrollmentStartDate ?? "")
      )
      .map((e) => ({
        ...e,
        PreschoolName: preschoolNameMap[e.PreschoolCode] || e.PreschoolName,
      }));

    agencies.push({
      key: reportingLEA,
      leaName: leaNameMap[reportingLEA] || reportingLEA,
      currentChld,
      isCurrent: openEnded.length > 0,
      famiRecords,
      enrlRecords,
    });
  });

  return agencies.sort((a, b) =>
    (b.currentChld.EffectiveStartDate ?? "").localeCompare(
      a.currentChld.EffectiveStartDate ?? ""
    )
  );
}

function AgencyHeaderLabel({
  agency,
  childName,
}: {
  agency: Agency;
  childName: string;
}) {
  const c = agency.currentChld;
  return (
    <span>
      {childName} <span className="mx-1">&middot;</span> {agency.leaName}
      {c.EffectiveStartDate && (
        <span className="ms-2 child-data-accordion-header-meta">
          {agency.isCurrent
            ? `(current since ${formatDateOnly(c.EffectiveStartDate)})`
            : `(historical: ${formatDateOnly(c.EffectiveStartDate)}${
                c.EffectiveEndDate
                  ? ` – ${formatDateOnly(c.EffectiveEndDate)}`
                  : ""
              })`}
        </span>
      )}
    </span>
  );
}

function AgencyContent({
  agency,
  coreData,
}: {
  agency: Agency;
  coreData: CoreDataModel;
}) {
  const c = agency.currentChld;
  const childName = getChildName(c);

  return (
    <>
      <ReviewSection icon={<FiUser />} title="Child Information">
        <div className="row">
          <ReviewField label="Child Name" value={childName} />
          <ReviewField label="CSPPID" value={c.CSPPID} />
          <ReviewField
            label="Date of Birth"
            value={formatDateOnly(c.ChildDateOfBirth)}
          />
          <ReviewField label="Gender" value={getGenderText(c.ChildGender)} />
          <ReviewField
            label="Hispanic Indicator"
            value={getHispanicIndicatorText(
              c.ChildEthnicity,
              c.ChildEthnicityMissingIndicator
            )}
          />
          <ReviewField
            label="Race(s)"
            value={getRaceText(
              [
                c.ChildRace1,
                c.ChildRace2,
                c.ChildRace3,
                c.ChildRace4,
                c.ChildRace5,
              ],
              coreData
            )}
          />
          <ReviewField
            label="IEP Status"
            value={getIEPStatusText(c.IEPIFSPIndicator, coreData)}
          />
        </div>
      </ReviewSection>

      <ReviewSection icon={<FiUsers />} title="Family Information">
        {agency.famiRecords.length === 0 ? (
          <p className="mb-0 text-muted">
            No family records found for this agency.
          </p>
        ) : (
          agency.famiRecords.map((f, index) => {
            const hoh1 = [
              f.HeadOfHousehold1FirstName,
              f.HeadOfHousehold1MiddleName,
              f.HeadOfHousehold1LastName,
            ]
              .filter(Boolean)
              .join(" ");
            const hoh2 = [
              f.HeadOfHousehold2FirstName,
              f.HeadOfHousehold2MiddleName,
              f.HeadOfHousehold2LastName,
            ]
              .filter(Boolean)
              .join(" ");
            const homeAddress = [
              f.ResidentialAddress1,
              f.ResidentialAddress2,
              f.ResidentialCity,
              f.ResidentialState,
              f.ResidentialZip,
            ]
              .filter(Boolean)
              .join(", ");
            const mailingAddress = [
              f.MailingAddress1,
              f.MailingAddress2,
              f.MailingCity,
              f.MailingState,
              f.MailingZip,
            ]
              .filter(Boolean)
              .join(", ");

            return (
              <div
                key={f.LocalFamilyId || index}
                className={index > 0 ? "row pt-3 mt-3 border-top" : "row"}
              >
                <ReviewField label="FICN" value={f.LocalFamilyId} />
                <ReviewField label="Head of Household 1" value={hoh1} />
                <ReviewField
                  label="Head of Household 1 Need(s)"
                  value={getServiceReasonsText(f, "HeadOfHousehold1", coreData)}
                />
                {hoh2 && (
                  <>
                    <ReviewField label="Head of Household 2" value={hoh2} />
                    <ReviewField
                      label="Head of Household 2 Need(s)"
                      value={getServiceReasonsText(
                        f,
                        "HeadOfHousehold2",
                        coreData
                      )}
                    />
                  </>
                )}
                <ReviewField
                  label="Home Address"
                  value={homeAddress}
                  fullWidth
                />
                {mailingAddress && (
                  <ReviewField
                    label="Mailing Address"
                    value={mailingAddress}
                    fullWidth
                  />
                )}
              </div>
            );
          })
        )}
      </ReviewSection>

      <ReviewSection icon={<FiBookOpen />} title="Enrollment Information">
        {agency.enrlRecords.length === 0 ? (
          <p className="mb-0 text-muted">
            No enrollment records found for this agency.
          </p>
        ) : (
          agency.enrlRecords.map((e, index) => (
            <div
              key={e.Id || index}
              className={index > 0 ? "row pt-3 mt-3 border-top" : "row"}
            >
              <ReviewField
                label="School"
                value={e.PreschoolName || e.PreschoolCode}
              />
              <ReviewField
                label="Enrollment Start Date"
                value={formatDateOnly(e.EnrollmentStartDate)}
              />
              <ReviewField
                label="Enrollment End Date"
                value={
                  e.EnrollmentEndDate ? formatDateOnly(e.EnrollmentEndDate) : ""
                }
              />
              <ReviewField
                label="Contract Type"
                value={getContractTypeText(e.ContractType, coreData)}
              />
              <ReviewField
                label="Program Type"
                value={getProgramTypeText(e.ProgramType, coreData)}
              />
              <ReviewField
                label="Eligibility Status"
                value={getEligibilityStatusText(e.EligibilityStatus, coreData)}
              />
            </div>
          ))
        )}
      </ReviewSection>
    </>
  );
}

const csvRow = (values: Array<string | number | null | undefined>) =>
  values.map((v) => escapeCsv(String(v ?? ""))).join(",");

function buildCsv(agencies: Agency[], coreData: CoreDataModel): string {
  const lines: string[] = [];
  const row = csvRow;

  const c0 = agencies[0].currentChld;
  lines.push(
    "Child Information",
    row([
      "CSPPID",
      "Child Name",
      "Date of Birth",
      "Gender",
      "Hispanic Indicator",
      "Race(s)",
      "IEP Status",
    ]),
    row([
      c0.CSPPID,
      getChildName(c0),
      formatDateOnly(c0.ChildDateOfBirth),
      getGenderText(c0.ChildGender),
      getHispanicIndicatorText(
        c0.ChildEthnicity,
        c0.ChildEthnicityMissingIndicator
      ),
      getRaceText(
        [
          c0.ChildRace1,
          c0.ChildRace2,
          c0.ChildRace3,
          c0.ChildRace4,
          c0.ChildRace5,
        ],
        coreData
      ),
      getIEPStatusText(c0.IEPIFSPIndicator, coreData),
    ]),
    "",
    "Family Information",
    row([
      "Agency",
      "FICN",
      "Head of Household 1",
      "Head of Household 1 Need(s)",
      "Head of Household 2",
      "Head of Household 2 Need(s)",
      "Home Address",
      "Mailing Address",
    ])
  );
  agencies.forEach((agency) => {
    agency.famiRecords.forEach((f) => {
      const hoh1 = [
        f.HeadOfHousehold1FirstName,
        f.HeadOfHousehold1MiddleName,
        f.HeadOfHousehold1LastName,
      ]
        .filter(Boolean)
        .join(" ");
      const hoh2 = [
        f.HeadOfHousehold2FirstName,
        f.HeadOfHousehold2MiddleName,
        f.HeadOfHousehold2LastName,
      ]
        .filter(Boolean)
        .join(" ");
      const homeAddress = [
        f.ResidentialAddress1,
        f.ResidentialAddress2,
        f.ResidentialCity,
        f.ResidentialState,
        f.ResidentialZip,
      ]
        .filter(Boolean)
        .join(", ");
      const mailingAddress = [
        f.MailingAddress1,
        f.MailingAddress2,
        f.MailingCity,
        f.MailingState,
        f.MailingZip,
      ]
        .filter(Boolean)
        .join(", ");
      lines.push(
        row([
          agency.leaName,
          f.LocalFamilyId,
          hoh1,
          getServiceReasonsText(f, "HeadOfHousehold1", coreData),
          hoh2,
          hoh2 ? getServiceReasonsText(f, "HeadOfHousehold2", coreData) : "",
          homeAddress,
          mailingAddress,
        ])
      );
    });
  });
  lines.push(
    "",
    "Enrollment Information",
    row([
      "Agency",
      "School",
      "Enrollment Start Date",
      "Enrollment End Date",
      "Contract Type",
      "Program Type",
      "Eligibility Status",
    ])
  );
  agencies.forEach((agency) => {
    agency.enrlRecords.forEach((e) => {
      lines.push(
        row([
          agency.leaName,
          e.PreschoolName || e.PreschoolCode,
          formatDateOnly(e.EnrollmentStartDate),
          e.EnrollmentEndDate ? formatDateOnly(e.EnrollmentEndDate) : "",
          getContractTypeText(e.ContractType, coreData),
          getProgramTypeText(e.ProgramType, coreData),
          getEligibilityStatusText(e.EligibilityStatus, coreData),
        ])
      );
    });
  });

  return "﻿" + lines.join("\r\n");
}

function downloadCsv(csv: string, fileName: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ChildDataDetail({ csppid }: { csppid: string }) {
  const coreData = useCoreData();
  const childData = useStateChildData(csppid).data;
  const { reportContentRef, exportToPDF, handlePrint } = useReportExport();

  const chld = (childData?.chld ?? []) as WithPreschoolName<CHLD>[];
  const fami = childData?.fami ?? [];
  const enrl = (childData?.enrl ?? []) as WithPreschoolName<ENRL>[];

  const leaList = useChildEntities("0-").data;
  const leaEsiidPaths = Array.from(
    new Set(chld.map((c) => c.ReportingLEA).filter(Boolean))
  )
    .map((code) => leaList?.find((l) => l.CDSCode === code)?.ESIID)
    .filter(Boolean)
    .map((esiid) => `0-${esiid}-`);
  const preschoolNameMap = usePreschoolNames(leaEsiidPaths).data;

  const leaNameMap: Record<string, string> = {};
  leaList?.forEach((l) => {
    leaNameMap[l.CDSCode] = l.LEAName;
  });

  const agencies = buildAgencies(
    chld,
    fami,
    enrl,
    preschoolNameMap,
    leaNameMap
  );

  const [openKeys, setOpenKeys] = useState<string[]>(() =>
    agencies[0] ? [agencies[0].key] : []
  );
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const exportTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (exportTimeoutRef.current !== null) {
        globalThis.clearTimeout(exportTimeoutRef.current);
      }
    };
  }, []);

  if (agencies.length === 0) {
    return (
      <Card className="card-custom shadow-none">
        <p className="mb-0 text-muted text-center py-4">
          No child records were found for CSPPID &quot;{csppid}&quot;.
        </p>
      </Card>
    );
  }

  const handleAccordionSelect = (eventKey: AccordionEventKey) => {
    setOpenKeys(
      Array.isArray(eventKey) ? eventKey : eventKey ? [eventKey] : []
    );
  };

  const expandAllAgencies = () => setOpenKeys(agencies.map((a) => a.key));

  const runAfterExpand = (action: () => void) => {
    expandAllAgencies();
    if (exportTimeoutRef.current !== null) {
      globalThis.clearTimeout(exportTimeoutRef.current);
    }
    exportTimeoutRef.current = globalThis.setTimeout(() => {
      exportTimeoutRef.current = null;
      action();
    }, 400);
  };

  const handleExportPDF = () => {
    setIsExportingPdf(true);
    runAfterExpand(() => {
      exportToPDF({
        fileName: `child-data-${csppid}.pdf`,
        landscape: false,
        hideNoPrint: true,
      });
      globalThis.setTimeout(() => setIsExportingPdf(false), 1000);
    });
  };

  const handlePrintClick = () => {
    setIsExportingPdf(false);
    runAfterExpand(handlePrint);
  };

  const handleExportCSV = () => {
    downloadCsv(buildCsv(agencies, coreData), `child-data-${csppid}.csv`);
  };

  return (
    <>
      <style>{`
        @media print {
          .report-filter-no-print { display: none !important; }
          body * { visibility: hidden !important; }
          .report-print-area, .report-print-area * { visibility: visible !important; }
          .report-print-area {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
          }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="report-filter-no-print d-flex justify-content-end gap-2 mb-3">
        <button
          type="button"
          className="btn btn-outline-secondary d-flex align-items-center gap-2"
          onClick={handleExportPDF}
        >
          <FiFileText size={15} /> PDF
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary d-flex align-items-center gap-2"
          onClick={handleExportCSV}
        >
          <FiGrid size={15} /> CSV
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary d-flex align-items-center gap-2"
          onClick={handlePrintClick}
        >
          <FiPrinter size={15} /> Print
        </button>
      </div>

      <div
        ref={reportContentRef}
        className={`report-print-area${isExportingPdf ? " child-data-pdf-export" : ""}`}
      >
        {agencies.length === 1 ? (
          isExportingPdf ? (
            <div>
              <h6 className="fw-semibold mb-3" style={{ color: "#0d2d4a" }}>
                <AgencyHeaderLabel
                  agency={agencies[0]}
                  childName={getChildName(agencies[0].currentChld)}
                />
              </h6>
              <AgencyContent agency={agencies[0]} coreData={coreData} />
            </div>
          ) : (
            <Card className="card-custom shadow-none">
              <h6 className="fw-semibold mb-3" style={{ color: "#0d2d4a" }}>
                <AgencyHeaderLabel
                  agency={agencies[0]}
                  childName={getChildName(agencies[0].currentChld)}
                />
              </h6>
              <AgencyContent agency={agencies[0]} coreData={coreData} />
            </Card>
          )
        ) : (
          <Accordion
            activeKey={openKeys}
            onSelect={handleAccordionSelect}
            alwaysOpen
            className="child-data-accordion"
          >
            {agencies.map((agency) => (
              <Accordion.Item eventKey={agency.key} key={agency.key}>
                <Accordion.Header>
                  <AgencyHeaderLabel
                    agency={agency}
                    childName={getChildName(agency.currentChld)}
                  />
                </Accordion.Header>
                <Accordion.Body>
                  <AgencyContent agency={agency} coreData={coreData} />
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
      </div>
    </>
  );
}
